import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Music as MusicIcon, Disc, Share2 } from "lucide-react";
import JazzHeader from "../homepage_components/JazzHeader";
import JazzFooter from "../homepage_components/JazzFooter";
import SEO from "../components/SEO";
import { UserMusicService } from "../services/UserMusicServices";
import { useComingSoon } from "../utils/useComingSoon";

export default function MusicDetails() {
    const { id } = useParams();
    const [album, setAlbum] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { showComingSoon } = useComingSoon();

    const fetchAlbumDetails = useCallback(async () => {
        try {
            const data = await UserMusicService.getMusicById(id);
            setAlbum(data);
        } catch (err) {
            console.error("Failed to fetch album details:", err);
            setError("The release you're looking for could not be found.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchAlbumDetails();
    }, [fetchAlbumDetails]);

    if (loading) {
        return (
            <div className="min-h-screen bg-white text-black">
                <JazzHeader />
                <main className="pt-24 sm:pt-32 pb-16 sm:pb-24">
                    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 animate-pulse">
                        <div className="h-4 w-48 bg-gray-200 mb-12 rounded" />
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                            <div className="lg:col-span-5 space-y-8">
                                <div className="aspect-square bg-gray-200 rounded shadow-lg" />
                                <div className="h-64 bg-gray-100 rounded border border-black/10" />
                            </div>
                            <div className="lg:col-span-7 pt-4 space-y-6">
                                <div className="h-4 w-32 bg-gray-200 rounded" />
                                <div className="h-16 w-3/4 bg-gray-200 rounded" />
                                <div className="h-4 w-16 bg-gray-200 rounded" />
                                <div className="space-y-4">
                                    <div className="h-4 w-full bg-gray-100 rounded" />
                                    <div className="h-4 w-full bg-gray-100 rounded" />
                                    <div className="h-4 w-2/3 bg-gray-100 rounded" />
                                </div>
                                <div className="pt-8 flex gap-6">
                                    <div className="h-4 w-32 bg-gray-200 rounded" />
                                    <div className="h-4 w-32 bg-gray-200 rounded" />
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
                <JazzFooter />
            </div>
        );
    }

    if (error || !album) {
        return (
            <div className="min-h-screen bg-white text-black">
                <JazzHeader />
                <div className="pt-32 pb-24 text-center max-w-xl mx-auto px-6">
                    <Disc size={64} className="mx-auto text-black/20 mb-6" />
                    <h2 className="text-3xl font-serif mb-4" style={{ fontFamily: "Playfair Display, serif" }}>Oops!</h2>
                    <p className="text-black/60 mb-8">{error || "Something went wrong."}</p>
                    <Link
                        to="/discography"
                        className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-black hover:text-jazz-brown transition-colors border-b border-black/20 hover:border-jazz-brown pb-1"
                    >
                        <ArrowLeft size={14} /> BACK TO DISCOGRAPHY
                    </Link>
                </div>
                <JazzFooter />
            </div>
        );
    }

    const streamingPlatforms = [
        { name: "Spotify", url: album.spotify_url, icon: "https://upload.wikimedia.org/wikipedia/commons/1/19/Spotify_logo_without_text.svg" },
        { name: "Apple Music", url: album.apple_music_url, icon: "https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg" },
        { name: "SoundCloud", url: album.soundcloud_url, icon: "https://upload.wikimedia.org/wikipedia/commons/a/af/Soundcloud_logo.svg" },
        { name: "Audiomack", url: album.audiomack_url, icon: "https://upload.wikimedia.org/wikipedia/commons/e/e1/Audiomack_logo_icon.png" },
    ].filter(p => p.url);

    /**
     * Renders a description as properly spaced paragraphs.
     * Handles:
     *  - Literal \n escape sequences stored in the DB as strings
     *  - Real newline characters (\n)
     *  - Paragraph grouping on double newlines (empty lines)
     *  - Sentence-based splitting for long "wall of text" blocks
     */
    const renderDescription = (text) => {
        if (!text) return null;
        // Normalise literal backslash-n sequences
        const normalised = text.replace(/\\n/g, '\n');

        // 1. Initial split by newlines
        let blocks = normalised.split(/\n+/).filter(block => block.trim().length > 0);

        // 2. Sentence splitting for long text blocks without newlines
        if (blocks.length === 1 && blocks[0].length > 100) {
            // Regex: match space after a period that is followed by a capital letter
            const sentenceSplit = blocks[0].split(/(?<=\.)\s+(?=[A-Z])/);
            if (sentenceSplit.length > 1) {
                blocks = sentenceSplit;
            }
        }

        return (
            <div className="space-y-6">
                {blocks.map((block, i) => (
                    <p key={i} className="last:mb-0">
                        {i === 0 && blocks.length > 1 ? `"${block.trim()}` :
                            i === blocks.length - 1 && blocks.length > 1 ? `${block.trim()}"` :
                                blocks.length === 1 ? `"${block.trim()}"` : block.trim()}
                    </p>
                ))}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-white text-black">
            <SEO
                title={album ? (album.feature_artist ? `${album.feature_artist} - ${album.title}` : album.title) : "Music Release"}
                description={album?.description}
                image={album?.cover_path}
                url={`/discography/${id}`}
                type="music.album"
            />
            <JazzHeader />

            <main className="pt-24 sm:pt-32 pb-16 sm:pb-24">
                <div className="max-w-[1200px] mx-auto px-4 sm:px-6">

                    {/* Back Nav */}
                    <div className="mb-12">
                        <Link
                            to="/discography"
                            className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-black hover:text-jazz-brown transition-colors group"
                        >
                            <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> BACK TO DISCOGRAPHY
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">

                        {/* Cover Art Section */}
                        <div className="lg:col-span-5 space-y-8">
                            <div className="relative aspect-square bg-gray-50 border border-black/5 overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.12)]">
                                <img
                                    src={album.cover_path}
                                    alt={album.title}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm text-black px-4 py-1.5 text-xs font-bold tracking-widest uppercase shadow-sm">
                                    {album.category || album.type}
                                </div>
                            </div>

                            {/* Listening Options */}
                            <div className="bg-[#FAFAFA] border border-black/5 p-8">
                                <h4 className="text-xs font-bold tracking-widest uppercase text-black mb-6 border-b border-black/10 pb-4">
                                    Stream & Purchase
                                </h4>
                                <div className="space-y-4">
                                    {streamingPlatforms.length > 0 ? (
                                        streamingPlatforms.map((platform) => (
                                            <Link
                                                key={platform.name}
                                                to={platform.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center justify-between p-4 bg-white border border-black/5 hover:border-jazz-brown/50 hover:shadow-md transition-all duration-300 group"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <span className="text-sm font-bold tracking-tight">{platform.name}</span>
                                                </div>
                                                <ExternalLink size={16} className="text-black group-hover:text-jazz-brown transition-colors" />
                                            </Link>
                                        ))
                                    ) : (
                                        <p className="text-sm text-black font-semibold italic">Streaming links coming soon.</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Info Section */}
                        <div className="lg:col-span-7 pt-4">
                            <div className="mb-8">
                                <p className="text-jazz-brown font-mono text-sm tracking-widest mb-4 uppercase">{album.year} • {album.type}</p>
                                <h1
                                    className="text-4xl sm:text-5xl lg:text-6xl font-normal leading-tight mb-6"
                                    style={{ fontFamily: "Playfair Display, serif" }}
                                >
                                    {album.feature_artist
                                        ? `${album.feature_artist} - ${album.title}`
                                        : album.title}
                                </h1>
                                <div className="w-16 h-[2px] bg-black/10" />
                            </div>

                            <div className="mt-12 pt-12 border-t border-black/5 flex flex-wrap gap-6">
                                <button
                                    onClick={() => showComingSoon({ title: "Share Release", message: "Social sharing and release links are coming soon to your dashboard." })}
                                    className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase hover:text-jazz-brown transition-colors"
                                >
                                    <Share2 size={16} /> SHARE RELEASE
                                </button>
                                <button
                                    onClick={() => showComingSoon({ title: "Add to Playlist", message: "Personalized music playlists are coming soon!" })}
                                    className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase hover:text-jazz-brown transition-colors"
                                >
                                    <MusicIcon size={16} /> ADD TO PLAYLIST
                                </button>
                            </div>

                            {/* Styled Dynamic Description Section - Relocated to Column */}
                            <div className="mt-12 group">
                                <div className="bg-[#111] text-white p-8 sm:p-12 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-48 h-48 bg-jazz-brown/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2 group-hover:bg-jazz-brown/20 transition-all duration-700" />
                                    <div className="relative z-10">
                                        <Disc size={120} className="absolute -right-16 -top-16 text-white/5 rotate-12 group-hover:rotate-45 transition-transform duration-1000" />
                                        <div
                                            className="text-xl sm:text-2xl font-normal italic leading-relaxed mb-8"
                                            style={{ fontFamily: "Crimson Pro, Georgia, serif" }}
                                        >
                                            {renderDescription(album.description)}
                                        </div>
                                        <div className="flex items-center gap-6">
                                            <div className="w-12 h-[1px] bg-jazz-brown" />
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-white/90">Release Insight</span>
                                                <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-white/40">{album.year} • {album.type}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>



                </div>
            </main>

            <JazzFooter />
        </div>
    );
}
